// MapContainer.jsx
import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from "react";
import Moment from "moment";
import { withScriptjs, withGoogleMap, GoogleMap, Marker, InfoWindow } from "react-google-maps";
//import { MarkerClusterer } from "react-google-maps/lib/components/addons/MarkerClusterer";
import Paho from "paho-mqtt";
import redpin from "./imgs/red_pin.png";
import greenpin from "./imgs/green_pin.png";
import "./map.css";
import { authenticationService, apiJson, fetchWithAuth } from "../../../jwt/_services";
import { history } from '../../../jwt/_helpers';
import { getStoredProductType } from "../../dashboard/product-filter";
import {
  filterOnlineMarkersByDevices,
  getHeartbeatProductIDs
} from "./heartbeat-product-filter";
import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  ButtonDropdown,
  Spinner
} from "reactstrap";

// -----------------------------------------------------------------------------
// Constants / helpers
// -----------------------------------------------------------------------------

const REACT_APP_GOOGLE_MAPS_API_KEY="AIzaSyAzQxot97t6EU-lgq8nPlT94TlTfqe-Xe0"

const GOOGLE_MAP_URL =
  "https://maps.googleapis.com/maps/api/js?key="+REACT_APP_GOOGLE_MAPS_API_KEY+"&v=3.exp&libraries=geometry,drawing,places";

const FILTER = {
  ALL: 0,
  ONLINE: 1,
  OFFLINE: 2,
};

// Dev-only: fake some devices as "online" for testing
const ENABLE_FAKE_ONLINE = false; // or use an env var
const FAKE_ONLINE_SERIALS = [
  "01025",
  "01254",
  "01260",
  "01784",
  "01842",
  "01582",
  "01407",
];

// Hard cap on how many markers TOTAL we will ever render at once
const MAX_OFFLINE_MARKERS = 1000; // tune this
const SHOW_ALL_VISIBLE_OFFLINE_ZOOM = 6;

// Decide cell size + per-cell cap based on zoom level
function getCellConfig(zoom) {
  const z = typeof zoom === "number" ? zoom : 4;

  // Very zoomed out (whole world)
  if (z <= 2) {
    return {
      cellSize: 10,  // degrees
      maxPerCell: 2, // super sparse
    };
  }

  // Fairly zoomed out (continents)
  if (z <= 4) {
    return {
      cellSize: 5,
      maxPerCell: 4,
    };
  }

  // Medium zoom (countries / states)
  if (z <= 6) {
    return {
      cellSize: 2,
      maxPerCell: 8,
    };
  }

  // Closer in
  if (z <= 8) {
    return {
      cellSize: 0.8,
      maxPerCell: 15,
    };
  }

  // Very zoomed in: basically show almost everything
  return {
    cellSize: 0.4,
    maxPerCell: 30,
  };
}

const MAP_OPTIONS = {
  zoomControl: true,
  zoomControlOptions: {
    position: 7,
  },
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
  styles: [
    { elementType: "geometry", stylers: [{ color: "#1D2C4D" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8EC3B9" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1A3646" }] },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4B6878" }],
    },
    {
      featureType: "administrative.land_parcel",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "administrative.land_parcel",
      elementType: "labels.text.fill",
      stylers: [{ color: "#64779E" }],
    },
    {
      featureType: "administrative.neighborhood",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "administrative.province",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4B6878" }],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.stroke",
      stylers: [{ color: "#334E87" }],
    },
    {
      featureType: "landscape.natural",
      elementType: "geometry",
      stylers: [{ color: "#023E58" }],
    },
    {
      featureType: "landscape.natural",
      elementType: "labels.text",
      stylers: [{ color: "#FFEB3B" }, { visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#283D6A" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6F9BA5" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1D2C4D" }],
    },
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [{ color: "#023E58" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#3C7680" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#304A7D" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98A5BE" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1D2C4D" }],
    },
    {
      featureType: "road.arterial",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#2C6675" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [{ color: "#255763" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#B0D5CE" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#023E58" }],
    },
    {
      featureType: "road.local",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98A5BE" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1D2C4D" }],
    },
    {
      featureType: "transit.line",
      elementType: "geometry.fill",
      stylers: [{ color: "#283D6A" }],
    },
    {
      featureType: "transit.station",
      elementType: "geometry",
      stylers: [{ color: "#3A4762" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#0E1626" }],
    },
    {
      featureType: "water",
      elementType: "labels.text",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#4E6D70" }],
    },
  ],
};

function shouldShowMarker(filter, isOnline) {
  if (filter === FILTER.ALL) return true;
  if (filter === FILTER.ONLINE) return isOnline;
  if (filter === FILTER.OFFLINE) return !isOnline;
  return true;
}

function formatSerialNumber(serialNumber) {
  if (serialNumber === null || serialNumber === undefined) return "";
  return String(serialNumber);
}

function getHeartbeatSerial(message) {
  return formatSerialNumber(message?.serial_num || message?.system_id?.base_serial_number);
}

function formatUsageSeconds(totalSeconds) {
  const d = Number(totalSeconds || 0);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor(d % 60);

  const hDisplay = h > 0 ? h + (h === 1 ? " hr " : " hrs ") : "";
  const mDisplay = m > 0 ? m + (m === 1 ? " min, " : " mins, ") : "";
  const sDisplay = s > 0 ? s + (s === 1 ? " sec" : " sec") : "";

  return hDisplay + mDisplay + sDisplay;
}

function getHeartbeatUsage(message) {
  const legacyUsage = Number(message?.life810 || 0) + Number(message?.life980 || 0);

  if (legacyUsage > 0) {
    return formatUsageSeconds(legacyUsage);
  }

  const wand = message?.wand || {};
  const novaUsage =
    Number(wand.usage_seconds_810 || 0) +
    Number(wand.usage_seconds_980 || 0) +
    Number(wand.usage_seconds_dual || 0);

  return formatUsageSeconds(novaUsage);
}

function getDevicePosition(device) {
  const lat = parseFloat(device.latitude);
  const lng = parseFloat(device.longitude);

  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return { lat, lng };
}

function getMapBoundsLiteral(map) {
  const bounds = map?.getBounds?.();

  if (!bounds) return null;

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  return {
    north: northEast.lat(),
    east: northEast.lng(),
    south: southWest.lat(),
    west: southWest.lng(),
  };
}

function isPositionInBounds(position, bounds) {
  if (!position || !bounds) return false;
  const isLatInBounds = position.lat >= bounds.south && position.lat <= bounds.north;
  const isLngInBounds = bounds.west <= bounds.east
    ? position.lng >= bounds.west && position.lng <= bounds.east
    : position.lng >= bounds.west || position.lng <= bounds.east;

  return isLatInBounds && isLngInBounds;
}

// -----------------------------------------------------------------------------
// Small presentational components
// -----------------------------------------------------------------------------

const DeviceInfoWindow = ({ device, iconMeta, isOnline, extraNearby = 0 }) => (
  <div style={{ width: "280px", height: "150px" }}>
    <h6>SN: {device.SerialNumber}</h6>
    <h6>Firmware: {device.firmware_version}</h6>
    <h6>Updated On: {Moment(device.firmware_date).format("LLL")}</h6>
    <h6>Last seen on: {Moment(device.geo_timestamp).format("LLL")}</h6>
    <h6>Owner: {iconMeta?.name || "N/A"}</h6>
    <h6>Email: {iconMeta?.email || "N/A"}</h6>
    {isOnline && (
      <h6>
        <a href={`/remotesupport/${device.SerialNumber}`}>Remote Support</a> |{" "}
        <a href={`/customize/${device.SerialNumber}`}>Customize</a>
      </h6>
    )}
    {extraNearby > 0 && (
      <h6>+{extraNearby} more units nearby in this area</h6>
    )}
  </div>
);

const DeviceRow = ({ onlineMarkers }) => {
  const handleClick = (serial) => {
    window.location.href = `/vitals/${serial}`;
  };

  return (
    <div className="mt-2">
      {onlineMarkers.length===0 &&
        <Fragment>
          <Row>
            <Col md="12" className="text-center">
              <div
                style={{ minWidth: "290px" }}
              >
              <h4 className="ml-2">No Devices</h4>
              </div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col lg="12">
              <hr className="new1" />
            </Col>
          </Row>
        </Fragment>
      }
      {onlineMarkers.map((device) => (
        <Fragment key={device.serial_number}>
          <Row>
            <Col md="12" className="text-center">
              <div
                style={{ minWidth: "290px", cursor: "pointer" }}
                onClick={() => handleClick(device.serial_number)}
              >
                <div className="float-left">
                  <img style={{ width: "23px", height: "23px" }} src={device.icon} alt="" />
                </div>
                {device.city ? (
                  <div className="float-left">
                    <span className="ml-2">
                      Serial: {device.serial_number} - {device.city}
                      {device.state ? `, ${device.state}` : ""}
                    </span>
                  </div>
                ) : (
                  <div className="float-left">
                    <h4 className="ml-2">Serial: {device.serial_number}</h4>
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col lg="12">
              <hr className="new1" />
            </Col>
          </Row>
        </Fragment>
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Map component (wrapped with Google Maps HOCs)
// -----------------------------------------------------------------------------

const RawMapWithMarkers = ({
  devices,
  devicesIcons,
  onlineSerials,
  markerDisplayStatus,
  selectedIndex,
  onSelectIndex,
}) => {
  const [zoom, setZoom] = useState(3.8);
  const [bounds, setBounds] = useState(null);
  const mapRef = useRef(null);

  const syncMapState = () => {
    if (mapRef.current) {
      const z = mapRef.current.getZoom();
      if (typeof z === "number") {
        setZoom(z);
      }
      setBounds(getMapBoundsLiteral(mapRef.current));
    }
  };

  const limitedMarkers = useMemo(() => {
    const { cellSize, maxPerCell } = getCellConfig(zoom);
    const showAllVisibleOffline = bounds && zoom >= SHOW_ALL_VISIBLE_OFFLINE_ZOOM;

    // For offline thinning: cellKey -> { totalOffline, shownOffline }
    const cellStatsOffline = new Map();
    const markersTemp = [];
    let offlineShown = 0;

    // If filter is ONLINE only, just render all online markers, no thinning
    if (markerDisplayStatus === FILTER.ONLINE) {
      devices.forEach((device, i) => {
        const isOnline = onlineSerials.has(formatSerialNumber(device.SerialNumber));
        if (!isOnline) return;

        const position = getDevicePosition(device);
        if (!position) return;

        const cellLat = Math.floor(position.lat / cellSize);
        const cellLng = Math.floor(position.lng / cellSize);
        const cellKey = `${cellLat}:${cellLng}`;

        markersTemp.push({
          device,
          iconMeta: devicesIcons[i],
          isOnline: true,
          index: i,
          cellKey,
          extraNearby: 0,
        });
      });

      return markersTemp;
    }

    // For ALL / OFFLINE filters, first split indices into online/offline
    const onlineIdx = [];
    const offlineIdx = [];

    devices.forEach((device, i) => {
      const isOnline = onlineSerials.has(formatSerialNumber(device.SerialNumber));

      // Respect the filter for OFFLINE case too
      if (!shouldShowMarker(markerDisplayStatus, isOnline)) return;

      if (isOnline) {
        onlineIdx.push(i);
      } else {
        offlineIdx.push(i);
      }
    });

    // 1) Always show ALL online markers (priority, no limits)
    onlineIdx.forEach((i) => {
      const device = devices[i];
      const position = getDevicePosition(device);
      if (!position) return;

      const cellLat = Math.floor(position.lat / cellSize);
      const cellLng = Math.floor(position.lng / cellSize);
      const cellKey = `${cellLat}:${cellLng}`;

      markersTemp.push({
        device,
        iconMeta: devicesIcons[i],
        isOnline: true,
        index: i,
        cellKey,
        // extraNearby will be filled from offline stats below
        extraNearby: 0,
      });
    });

    // 2) Thinning for OFFLINE markers only
    offlineIdx.forEach((i) => {
      const device = devices[i];

      const position = getDevicePosition(device);
      if (!position) return;

      const cellLat = Math.floor(position.lat / cellSize);
      const cellLng = Math.floor(position.lng / cellSize);
      const key = `${cellLat}:${cellLng}`;

      if (showAllVisibleOffline) {
        if (!isPositionInBounds(position, bounds)) return;

        markersTemp.push({
          device,
          iconMeta: devicesIcons[i],
          isOnline: false,
          index: i,
          cellKey: key,
          extraNearby: 0,
        });

        return;
      }

      const stats = cellStatsOffline.get(key) || {
        totalOffline: 0,
        shownOffline: 0,
      };

      stats.totalOffline += 1;

      if (stats.shownOffline < maxPerCell && offlineShown < MAX_OFFLINE_MARKERS) {
        stats.shownOffline += 1;
        offlineShown += 1;

        markersTemp.push({
          device,
          iconMeta: devicesIcons[i],
          isOnline: false,
          index: i,
          cellKey: key,
          extraNearby: 0,
        });
      }

      cellStatsOffline.set(key, stats);
    });

    // 3) Compute how many OFFLINE markers are hidden per cell
    const overflowMap = new Map();
    cellStatsOffline.forEach((stats, key) => {
      const extra = stats.totalOffline - stats.shownOffline;
      if (extra > 0) {
        overflowMap.set(key, extra);
      }
    });

    // 4) Attach extraNearby per marker (online + offline share same cellKey)
    return markersTemp.map((m) => ({
      ...m,
      extraNearby: overflowMap.get(m.cellKey) || 0,
    }));
  }, [devices, devicesIcons, onlineSerials, markerDisplayStatus, zoom, bounds]);

  return (
    <GoogleMap
      defaultZoom={3.8}
      defaultCenter={{ lat: 35, lng: -95.35 }}
      options={MAP_OPTIONS}
      ref={mapRef}
      onZoomChanged={syncMapState}
      onIdle={syncMapState}
    >
      {limitedMarkers.map(({ device, iconMeta, isOnline, index, extraNearby }) => {
        const iconUrl = isOnline ? greenpin : redpin;

        return (
          <Marker
            key={device.SerialNumber}
            onClick={() => onSelectIndex(index)}
            position={{
              lat: parseFloat(device.latitude),
              lng: parseFloat(device.longitude),
            }}
            zIndex={isOnline ? 999999 : 0}
            icon={{
              url: iconUrl,
              scaledSize: new window.google.maps.Size(15, 18),
            }}
          >
            {selectedIndex === index && (
              <InfoWindow onCloseClick={() => onSelectIndex(null)}>
                <DeviceInfoWindow
                  device={device}
                  iconMeta={iconMeta}
                  isOnline={isOnline}
                  extraNearby={extraNearby}
                />
              </InfoWindow>
            )}
          </Marker>
        );
      })}
    </GoogleMap>
  );
};



const MapWithMarkers = withScriptjs(withGoogleMap(RawMapWithMarkers));

// -----------------------------------------------------------------------------
// MQTT hook (cleaner, isolated side-effects)
// -----------------------------------------------------------------------------

/**
 * Heartbeat messages are expected to look roughly like:
 * {
 *   batch: true,
 *   serial_num: "12345",
 *   fw_ver: "...",
 *   life810: number,
 *   life980: number,
 *   ...
 * }
 */
function useHeartbeatMQTT({ currentUser, devices, onOnlineUpdate }) {
  const [client, setClient] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let mqttClient = null;

    const connectToMQTT = async () => {
      try {

        if (!authenticationService.currentUserValue) {
          // optionally navigate to login or just bail silently until auth finishes
          console.warn('[fetchWithAuth] No current user, redirecting to login.');
          // Redirect to login page
          const { pathname, search, hash } = history.location; // snapshot now
          // avoid pushing repeatedly if already on login
          if (pathname === '/authentication/login') return;

          history.push('/authentication/login', {
            from: { pathname, search, hash }, // plain POJO, not live location
          });

          return;
        }

        const connectionUrl = await authenticationService.getGlobalConnectionURL();
        if (!isMounted || connectionUrl.status !== "Success") return;

        const hash =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);

        mqttClient = new Paho.Client(
          connectionUrl.response,
          hash + currentUser.payload.sub
        );

        mqttClient.onConnectionLost = (resp) => {
          if (resp.errorCode !== 0) {
            console.log("onConnectionLost:", resp.errorMessage);
          }
        };

        mqttClient.onMessageArrived = (entry) => {
          try {
            const msg = JSON.parse(entry.payloadString);
            if (!getHeartbeatSerial(msg)) return;
            onOnlineUpdate(msg);
          } catch (e) {
            console.error("Invalid MQTT message:", e);
          }
        };

        mqttClient.connect({
          timeout: 3,
          useSSL: true,
          mqttVersion: 4,
          cleanSession: true,
          onSuccess: () => {
            console.log("MQTT connected");
            mqttClient.subscribe("#");
          },
          onFailure: (e) => {
            console.log("MQTT connection error", e);
          },
        });

        setClient(mqttClient);
      } catch (err) {
        console.error("connectToMQTT error", err);
      }
    };

    connectToMQTT();

    return () => {
      isMounted = false;
      if (mqttClient && mqttClient.isConnected()) {
        mqttClient.disconnect();
      }
    };
  }, [currentUser, devices, onOnlineUpdate]);

  return client;
}

// -----------------------------------------------------------------------------
// Main container
// -----------------------------------------------------------------------------

const MapContainerFull = () => {
  const currentUser = authenticationService.currentUserValue;

  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [devicesIcons, setDevicesIcons] = useState([]); // owner/name/email by device index
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [productType, setProductType] = useState(getStoredProductType);

  const [onlineMarkers, setOnlineMarkers] = useState([]); // [{ serial_number, icon, last_update, city, state, country, version, usage }]
  const [markerDisplayStatus, setMarkerDisplayStatus] = useState(FILTER.ALL);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentFilterLabel, setCurrentFilterLabel] = useState("All Units");
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const fetchUsers = useCallback(async () => {

    try {

      if (!authenticationService.currentUserValue) {
        // optionally navigate to login or just bail silently until auth finishes
        console.warn('[fetchWithAuth] No current user, redirecting to login.');
        // Redirect to login page
        const { pathname, search, hash } = history.location; // snapshot now
        // avoid pushing repeatedly if already on login
        if (pathname === '/authentication/login') return;

        history.push('/authentication/login', {
          from: { pathname, search, hash }, // plain POJO, not live location
        });

        return;
      }

      const url =
        "https://9xnwyna9id.execute-api.us-west-2.amazonaws.com" +
        process.env.REACT_APP_ENV +
        "get-users";

      const response = await fetchWithAuth(
        url,
        { method: 'GET' },
        {
          tokenType: 'id',
          authScheme: 'raw',
          timeoutMs: 28000,
          maxRetries: 2,
        }
      );

      const body = await response.json();
      if (body.status === "Success") {
        setUsers(body.response.Users);
      } else {
        console.warn("Failed to load users");
      }

    } catch (err) {
      console.error('getTopProceduresData error:', err);
    } finally {
      //setLoadingTopProcedures(false);
    }

  }, [currentUser]);

  const fetchDevices = useCallback(async () => {

    try {

      setDevicesLoaded(false);
      setDevices([]);
      setDevicesIcons([]);
      setSelectedMarkerIndex(null);

      if (!authenticationService.currentUserValue) {
        // optionally navigate to login or just bail silently until auth finishes
        console.warn('[fetchWithAuth] No current user, redirecting to login.');
        // Redirect to login page
        const { pathname, search, hash } = history.location; // snapshot now
        // avoid pushing repeatedly if already on login
        if (pathname === '/authentication/login') return;

        history.push('/authentication/login', {
          from: { pathname, search, hash }, // plain POJO, not live location
        });

        return;
      }

      const baseUrl =
        "https://9xnwyna9id.execute-api.us-west-2.amazonaws.com" +
        process.env.REACT_APP_ENV +
        "all-devices";

      const productResponses = await Promise.all(
        getHeartbeatProductIDs(productType).map(async (productID) => {
          const response = await fetchWithAuth(
            `${baseUrl}?ProductID=${productID}`,
            { method: 'GET' },
            {
              tokenType: 'id',
              authScheme: 'raw',
              timeoutMs: 28000,
              maxRetries: 2,
            }
          );

          return response.json();
        })
      );

      const failedResponse = productResponses.find((body) => body.status !== "Success");

      if (!failedResponse) {
        const devicesBySerial = new Map();

        productResponses.forEach((body) => {
          (body.results || []).forEach((device) => {
            if (!device || device.SerialNumber === undefined || device.SerialNumber === null) return;
            devicesBySerial.set(String(device.SerialNumber), device);
          });
        });

        const devicesResults = Array.from(devicesBySerial.values());
        setDevices(devicesResults);

        // Build icons metadata (N/A by default, filled if we find a matching user)
        const iconMeta = devicesResults.map((d, index) => {
          let name = "N/A";
          let email = "N/A";

          if (d.LoginID && users.length) {
            const user = users.find(
              (u) => u.Username.toString().toLowerCase() === d.LoginID.toString().toLowerCase()
            );

            if (user) {
              const firstName = user.Attributes.filter((a) => a.Name === "given_name").map(
                (n) => n.Value
              );
              const lastName = user.Attributes.filter((a) => a.Name === "family_name").map(
                (n) => n.Value
              );
              name = `${firstName} ${lastName}`;
              email = d.LoginID.toString().toLowerCase();
            }
          }

          return {
            id: index,
            serial_number: d.SerialNumber,
            name,
            email,
          };
        });

        setDevicesIcons(iconMeta);
        setDevicesLoaded(true);
      } else {
        console.warn("Failed to load devices");
      }

    } catch (err) {
      console.error('getTopProceduresData error:', err);
    } finally {
      //setLoadingTopProcedures(false);
    }

  }, [currentUser, productType, users]);

  useEffect(() => {
    const handleProductChange = () => {
      setProductType(getStoredProductType());
    };

    document.body.addEventListener("set-product-type", handleProductChange);
    return () => document.body.removeEventListener("set-product-type", handleProductChange);
  }, []);

  useEffect(() => {
    (async () => {
      await fetchUsers();
    })();
  }, [fetchUsers]);

  // After users are loaded, load devices (so we can resolve owner/name/email)
  useEffect(() => {
    if (users.length) {
      fetchDevices();
    }
  }, [users, fetchDevices]);

  // Dev helper: mark some devices as online even without MQTT
  useEffect(() => {
    if (!ENABLE_FAKE_ONLINE) return;
    if (!devices || devices.length === 0) return;

    setOnlineMarkers((prev) => {
      const next = [...prev];
      const existingSerials = new Set(prev.map((m) => m.serial_number));

      FAKE_ONLINE_SERIALS.forEach((serial) => {
        if (existingSerials.has(formatSerialNumber(serial))) return;

        const device = devices.find((d) => formatSerialNumber(d.SerialNumber) === formatSerialNumber(serial));
        if (!device) return;

        const city = device.geo_city || "Unknown";
        const state = device.geo_state || "Unknown";
        const country = device.geo_country || "Unknown";

        next.push({
          serial_number: serial,
          icon: greenpin,               // forces "online"
          last_update: new Date(),
          city,
          state,
          country,
          version: device.firmware_version || "N/A",
          usage: "fake dev online",     // or build real text if you want
        });
      });

      return next;
    });
  }, [devices]);


  // ---------------------------------------------------------------------------
  // Online / offline handling (MQTT heartbeat)
  // ---------------------------------------------------------------------------

  const handleHeartbeatMessage = useCallback(
    (msg) => {
      // msg.serial_num, msg.fw_ver, msg.life810, msg.life980, etc.
      const serialNumber = getHeartbeatSerial(msg);

      if (!serialNumber) return;

      const device = devices.find(
        (item) => formatSerialNumber(item.SerialNumber) === serialNumber
      );

      // MQTT is global; ignore heartbeats outside the selected product dataset.
      if (!device) return;

      setOnlineMarkers((prev) => {
        const existingIndex = prev.findIndex(
          (m) => formatSerialNumber(m.serial_number) === serialNumber
        );

        // find device geo info
        const city = device?.geo_city || "Unknown";
        const state = device?.geo_state || "Unknown";
        const country = device?.geo_country || "Unknown";
        const usage = getHeartbeatUsage(msg);

        const updatedMarker = {
          serial_number: serialNumber,
          icon: greenpin,
          last_update: new Date(),
          city,
          state,
          country,
          version: msg.fw_ver || msg.firmware_version || device?.firmware_version || "N/A",
          usage,
        };

        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = {
            ...next[existingIndex],
            ...updatedMarker,
          };
          return next;
        }

        return [...prev, updatedMarker];
      });
    },
    [devices]
  );

  // Set icon red/green depending on last_update
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineMarkers((prev) => {
        const now = Date.now();
        return prev.map((m) => {
          const isStale = now - m.last_update.getTime() > 20000;
          return {
            ...m,
            icon: isStale ? redpin : greenpin,
          };
        });
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const visibleOnlineMarkers = useMemo(
    () => filterOnlineMarkersByDevices(onlineMarkers, devices),
    [onlineMarkers, devices]
  );

  // Derive a Set of selected-product serials that are currently online.
  const onlineSerials = useMemo(() => {
    return new Set(
      visibleOnlineMarkers
        .filter((m) => m.icon === greenpin)
        .map((m) => m.serial_number)
    );
  }, [visibleOnlineMarkers]);

  // Initialize MQTT hook
  useHeartbeatMQTT({
    currentUser,
    devices,
    onOnlineUpdate: handleHeartbeatMessage,
  });

  // ---------------------------------------------------------------------------
  // UI handlers
  // ---------------------------------------------------------------------------

  const toggleDropDown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const selectDropDownItem = (id, title) => {
    setMarkerDisplayStatus(id);
    setCurrentFilterLabel(title);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      <Row>
        <Col lg="12" xl="12" xxl="12">
          <div
            style={{
              position: "fixed",
              inset: 0,            // top:0, right:0, bottom:0, left:0
              width: "100vw",
              height: "100vh",
              overflow: "hidden",
              zIndex: 1,
              padding: 15
            }}
            >
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                {/* Map should render immediately, even if devices are still loading */}
                <MapWithMarkers
                  googleMapURL={GOOGLE_MAP_URL}
                  loadingElement={<div style={{ height: "100%" }} />}
                  containerElement={<div style={{ height: "100%" }} />}
                  mapElement={<div style={{ height: "100%" }} />}
                  devices={devices}
                  devicesIcons={devicesIcons}
                  onlineSerials={onlineSerials}
                  markerDisplayStatus={markerDisplayStatus}
                  selectedIndex={selectedMarkerIndex}
                  onSelectIndex={setSelectedMarkerIndex}
                />

                {/* Overlay loader on top of the map while devices are loading */}
                {(!devicesLoaded || devicesIcons.length === 0) && (
                  <div className="map-loading-overlay">
                    <div className="map-loading-content">
                      <div className="map-loading-spinner" />
                      <div>Loading Devices</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </Col>
      </Row>
    </div>
  );
};

export default MapContainerFull;
